import { createRoute, useNavigate, Navigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, Search } from "lucide-react";
import { useSession } from "@/frontend/auth-client";
import { orpc, orpcClient, queryClient } from "@/frontend/orpc-client";
import { reportFrontendError } from "@/frontend/lib/error-reporter";
import { ACCOUNT_TYPE } from "@/server/constants/persona";
import { Route as dashboardLayout } from "./_layout";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { SidebarTrigger } from "@/frontend/components/ui/sidebar";
import { cn } from "@/frontend/lib/utils";
import {
  useActiveMemberQuery,
  usePatientListCountsQuery,
  usePatientsInfiniteQuery,
} from "@/frontend/queries/dashboard/patients";
import { PageShell } from "@/frontend/components/dashboard/PageShell";
import { useWorkspacePersona } from "@/frontend/components/dashboard/individual/useWorkspacePersona";
import { PatientTable } from "@/frontend/components/dashboard/patients/PatientTable";
import { AddPatientSheet } from "@/frontend/components/dashboard/patients/AddPatientSheet";
import { QuotaWarningBanner } from "@/frontend/components/dashboard/QuotaWarningBanner";
import { useDebounce } from "@/frontend/hooks/useDebounce";
import {
  isSelfPatientConflict,
  toSegment,
  toSortBy,
  toSortDir,
} from "@/frontend/utils/dashboard/patient-table";
import {
  SEGMENTS,
  type PatientSortBy,
  type PatientSortDir,
  type PatientTableFilters,
  type SegmentId,
} from "@/frontend/types/dashboard/patients";

/** Table state carried in the URL. All optional, so a bare `/dashboard/patients` link works. */
type PatientsSearch = {
  q?: string;
  segment?: SegmentId;
  sortBy?: PatientSortBy;
  sortDir?: PatientSortDir;
  /** Legacy deep link from the old master-detail page. Redirected on mount. */
  patientMembershipId?: string;
};

/**
 * Returns the owner's own patient id in an individual workspace, for the route
 * to redirect to. Returns null when the workspace is not individual, when the
 * active org cannot be read, or when the workspace already holds someone else's
 * patient. All three fall back to the patient table.
 */
async function resolveIndividualSelfPatientId(): Promise<string | null> {
  let activeOrg: Awaited<ReturnType<typeof orpcClient.org.getFullOrganization>>;
  try {
    activeOrg = await queryClient.ensureQueryData(
      orpc.org.getFullOrganization.queryOptions({ input: {} }),
    );
  } catch (error) {
    reportFrontendError({
      message: `patients route: could not read the active workspace, falling back to the patient table (${
        error instanceof Error ? error.message : String(error)
      })`,
      stack: error instanceof Error ? (error.stack ?? "") : "",
    });
    return null;
  }

  if (activeOrg?.accountType !== ACCOUNT_TYPE.INDIVIDUAL) return null;

  try {
    const { patientMembershipId } = await orpcClient.org.ensureSelfPatient({});
    return patientMembershipId;
  } catch (error) {
    if (!isSelfPatientConflict(error)) throw error;
    reportFrontendError({
      message:
        "patients route: this individual workspace already holds another patient, falling back to the patient table",
      stack: error instanceof Error ? (error.stack ?? "") : "",
    });
    return null;
  }
}

export const Route = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/dashboard/patients",
  component: PatientsPage,
  beforeLoad: async () => {
    const selfPatientId = await resolveIndividualSelfPatientId();
    if (!selfPatientId) return;
    throw redirect({
      to: "/dashboard/patients/$patientId/summary",
      params: { patientId: selfPatientId },
      replace: true,
    });
  },
  validateSearch: (search: Record<string, unknown>): PatientsSearch => ({
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
    segment: toSegment(search.segment),
    sortBy: toSortBy(search.sortBy),
    sortDir: toSortDir(search.sortDir),
    patientMembershipId:
      typeof search.patientMembershipId === "string" && search.patientMembershipId.length > 0
        ? search.patientMembershipId
        : undefined,
  }),
});

/**
 * The workspace patient table. Search, chip and sort live in the URL so any
 * view is linkable; rows load 50 at a time as the scroll nears the end.
 */
function PatientsPage() {
  const rawSearch = Route.useSearch();
  const segment = rawSearch.segment ?? "all";
  const navigate = useNavigate();
  const { data: session } = useSession();
  const isPlatformAdmin = session?.user?.role === "admin";

  const activeMemberQuery = useActiveMemberQuery(!!session && !isPlatformAdmin);
  const roles = (activeMemberQuery.data?.role ?? "")
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
  const canManage = isPlatformAdmin || roles.some((role) => ["admin", "owner", "member"].includes(role));
  // An individual workspace holds one patient, so adding another is refused server-side too.
  const { isIndividual } = useWorkspacePersona();
  const canAddPatient = canManage && !isIndividual;

  const [searchInput, setSearchInput] = useState(rawSearch.q ?? "");
  const [addOpen, setAddOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);

  const filters: PatientTableFilters = {
    q: debouncedSearch.trim() || undefined,
    segment,
    sortBy: rawSearch.sortBy,
    sortDir: rawSearch.sortDir,
  };
  const { data: counts } = usePatientListCountsQuery();
  const patientsQuery = usePatientsInfiniteQuery(filters);

  /** Merges one filter change into the URL, replacing the history entry so tweaks don't stack up in Back. */
  const setSearch = (next: Partial<PatientsSearch>) => {
    navigate({
      to: "/dashboard/patients",
      search: { ...rawSearch, patientMembershipId: undefined, ...next },
      replace: true,
    });
  };

  if (rawSearch.patientMembershipId) {
    return (
      <Navigate
        to="/dashboard/patients/$patientId/summary"
        params={{ patientId: rawSearch.patientMembershipId }}
        replace
      />
    );
  }

  const rows = patientsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const hasFilters = !!debouncedSearch.trim() || segment !== "all";

  return (
    <div className="flex min-h-svh min-w-[64rem] flex-col lg:h-svh lg:min-h-0 lg:overflow-hidden">
      <header className="border-b">
        <PageShell>
          <div className="flex items-center gap-3 py-3">
            <SidebarTrigger className="text-muted-foreground" />
            <h1 className="text-lg font-semibold tracking-tight">Patients</h1>

            {canAddPatient && (
              <Button className="ml-auto h-8 gap-1.5" onClick={() => setAddOpen(true)}>
                Add patient
                <Plus className="size-4" />
              </Button>
            )}
          </div>

          <div className="pb-3 empty:hidden">
            <QuotaWarningBanner />
          </div>
        </PageShell>
      </header>

      <div className="border-b">
        <PageShell>
          <div className="flex flex-wrap items-center gap-3 py-3">
            <div className="relative min-w-[16rem] flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by name or email"
                className="h-9 pl-9 text-xs"
              />
            </div>

            <div className="flex flex-wrap items-center gap-1 rounded-lg border p-1">
              {SEGMENTS.map((chip) => {
                const count = counts?.[chip.countKey];
                const isActive = segment === chip.id;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => setSearch({ segment: chip.id })}
                    className={cn(
                      "flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs transition-colors",
                      isActive
                        ? "bg-accent font-medium text-foreground"
                        : "text-muted-foreground hover:bg-accent/50",
                    )}
                  >
                    {chip.label}
                    {count !== undefined && (
                      <span className="tabular-nums text-muted-foreground">{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </PageShell>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <PatientTable
          rows={rows}
          listKey={JSON.stringify(filters)}
          isLoading={patientsQuery.isLoading}
          hasFilters={hasFilters}
          hasNextPage={patientsQuery.hasNextPage}
          isFetchingNextPage={patientsQuery.isFetchingNextPage}
          onLoadMore={() => {
            if (!patientsQuery.isFetching) void patientsQuery.fetchNextPage();
          }}
          isLoadMoreError={patientsQuery.isFetchNextPageError}
          onRetryLoadMore={() => void patientsQuery.fetchNextPage()}
          header={{
            sortBy: rawSearch.sortBy,
            sortDir: rawSearch.sortDir,
            onSortChange: (sortBy, sortDir) => setSearch({ sortBy, sortDir }),
          }}
          onAddPatient={() => setAddOpen(true)}
          onClearFilters={() => {
            setSearchInput("");
            setSearch({ q: undefined, segment: undefined });
          }}
        />
        <PageShell>
          <div className="flex items-center justify-between gap-3 py-2.5">
            <span className="text-caption text-muted-foreground">
              {counts
                ? `${counts.total.toLocaleString()} patients · ${counts.recordTotal.toLocaleString()} records aggregated`
                : ""}
            </span>
            <span className="text-caption text-muted-foreground tabular-nums">
              {counts ? `Showing ${rows.length} of ${counts.total}` : ""}
            </span>
          </div>
        </PageShell>
      </div>

      <AddPatientSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(patientMembershipId) =>
          navigate({
            to: "/dashboard/patients/$patientId/onboarding",
            params: { patientId: patientMembershipId },
          })
        }
      />
    </div>
  );
}
